import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrowserMultiFormatReader } from '@zxing/browser';
import Swal from 'sweetalert2';
import { ArrowLeft, Image, Zap, ZapOff, QrCode } from 'lucide-react';

const parseScannedIds = (scannedId) => {
  try {
    const parsed = JSON.parse(scannedId);
    if (parsed && Array.isArray(parsed.ids)) return parsed.ids;
    if (parsed && typeof parsed === 'object' && parsed.id) return [parsed.id];
  } catch (e) {}
  return [scannedId];
};

const LaporScanPage = () => {
  const navigate = useNavigate();
  const [scannerError, setScannerError] = useState('');
  const [scanMode, setScanMode] = useState('camera');
  const [flashOn, setFlashOn] = useState(false);
  const [flashSupported, setFlashSupported] = useState(false);
  const codeReaderRef = useRef(null);
  const videoTrackRef = useRef(null);
  const activeStreamRef = useRef(null);   // <-- simpan MediaStream aktif
  const activeVideoRef = useRef(null);    // <-- simpan elemen video aktif
  const readerContainerRef = useRef(null);
  const readerContainerId = 'lapor-scan-reader';

  // Fungsi stop kamera secara paksa — hentikan semua track di stream
  const stopCamera = () => {
    // 1. Stop semua track di MediaStream
    if (activeStreamRef.current) {
      activeStreamRef.current.getTracks().forEach(track => {
        try { track.stop(); } catch (e) {}
      });
      activeStreamRef.current = null;
    }
    // 2. Kosongkan srcObject video agar browser lepas kamera
    if (activeVideoRef.current) {
      try {
        activeVideoRef.current.pause();
        activeVideoRef.current.srcObject = null;
      } catch (e) {}
      activeVideoRef.current = null;
    }
    // 3. Reset ZXing reader
    if (codeReaderRef.current) {
      try { codeReaderRef.current.reset(); } catch (e) {}
      codeReaderRef.current = null;
    }
    videoTrackRef.current = null;
  };

  const buildCurrentUser = () => {
    const currentUser = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const myId = String(currentUser.id || '');
    const myName = `${currentUser.nama_depan || ''} ${currentUser.nama_belakang || ''}`.trim();
    return { myId, myName };
  };

  const receiveTitipan = async (scannedId) => {
    const { myId, myName } = buildCurrentUser();
    if (!myId) {
      Swal.fire({ icon: 'warning', title: 'User tidak ditemukan', text: 'Silakan login ulang.', confirmButtonText: 'Tutup' });
      return;
    }
    try {
      const res = await fetch('https://jne-cilacap.com/slip/public/api/handovers/receive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scanned_text: scannedId, user_id: myId, user_name: myName })
      });
      const json = await res.json();
      if (json.status === 'success') {
        Swal.fire('Berhasil', json.message || 'Barang handover berhasil diterima.', 'success');
        navigate('/lapor');
        return;
      }
    } catch (e) {
      console.warn('API receive failed, falling back to local storage:', e);
    }

    const idsToReceive = parseScannedIds(scannedId);
    if (!idsToReceive.length) {
      Swal.fire({ icon: 'error', title: 'QR tidak valid', text: 'Data QR tidak sesuai.', confirmButtonText: 'Tutup' });
      return;
    }

    const rawTitipan = JSON.parse(localStorage.getItem('data_titipan') || '[]');
    const updated = rawTitipan.map((t) => {
      if (idsToReceive.includes(t.id)) {
        const isFinalRecipient = String(t.recipient_id) === myId;
        const newStatus = isFinalRecipient ? 'Selesai' : 'Diserahkan';
        const baseTimeline = t.timeline || [{ status: 'Dibuat', actor_id: t.sender_id, actor_name: t.sender_name, timestamp: t.timestamp, notes: `Handover dibuat oleh ${t.sender_name}` }];
        
        const lastEntry = baseTimeline[baseTimeline.length - 1];
        const isDuplicateEntry = lastEntry &&
          lastEntry.status === newStatus &&
          String(lastEntry.actor_id) === String(myId);

        const newTimeline = isDuplicateEntry 
          ? baseTimeline 
          : [...baseTimeline, { 
              status: newStatus, 
              actor_id: myId, 
              actor_name: myName, 
              timestamp: new Date().toISOString(), 
              notes: isFinalRecipient ? `Diterima oleh Penerima Akhir (${myName})` : `Diterima oleh Perantara (${myName})` 
            }];

        return {
          ...t,
          status: newStatus,
          current_holder_id: myId,
          current_holder_name: myName,
          pending_receiver_id: null,
          pending_receiver_name: null,
          confirmation_status: 'confirmed',
          receive_time: new Date().toISOString(),
          receiver_id: myId,
          receiver_name: myName,
          timeline: newTimeline
        };
      }
      return t;
    });

    localStorage.setItem('data_titipan', JSON.stringify(updated));
    localStorage.setItem('data_titipan_last_fetch', '0'); // Invalidate cache agar data terbaru diambil dari server
    Swal.fire('Berhasil', `${idsToReceive.length} titipan telah diterima secara offline`, 'success');
    navigate('/lapor');
  };

  const toggleFlash = async () => {
    const track = videoTrackRef.current;
    if (!track) return;
    try {
      const newState = !flashOn;
      await track.applyConstraints({ advanced: [{ torch: newState }] });
      setFlashOn(newState);
    } catch (e) {
      console.warn('Flash toggle failed:', e);
    }
  };

  const handleCloseScan = () => {
    stopCamera();
    navigate('/lapor');
  };

  const handlePhotoScan = async (files) => {
    if (!files?.length) return;
    const file = files[0];
    const fileUrl = URL.createObjectURL(file);
    setScanMode('gallery');

    const decodeImage = async (imageElement) => {
      const codeReader = new BrowserMultiFormatReader();
      // 1) Try decoding directly from the image element
      try {
        const result = await codeReader.decodeFromImageElement(imageElement);
        if (result?.getText()) return result.getText();
      } catch (err) {}

      // 2) Fallback: draw to canvas (scaled) and try decoding from canvas
      try {
        const w = imageElement.naturalWidth || imageElement.width || 0;
        const h = imageElement.naturalHeight || imageElement.height || 0;
        if (!w || !h) return null;
        const scale = Math.min(2, Math.max(1, 1200 / Math.max(w, h)));
        const cw = Math.round(w * scale);
        const ch = Math.round(h * scale);

        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(imageElement, 0, 0, cw, ch);

        try {
          const res = await codeReader.decodeFromCanvas(canvas);
          if (res?.getText()) return res.getText();
        } catch (e) {}

        // 3) Try rotated canvases (90/180/270) to handle orientation issues
        const rotations = [90, 180, 270];
        for (const rot of rotations) {
          const rCanvas = document.createElement('canvas');
          const rCtx = rCanvas.getContext('2d');
          if (rot % 180 === 0) { rCanvas.width = cw; rCanvas.height = ch; }
          else { rCanvas.width = ch; rCanvas.height = cw; }
          rCtx.translate(rCanvas.width / 2, rCanvas.height / 2);
          rCtx.rotate((rot * Math.PI) / 180);
          rCtx.drawImage(canvas, -cw / 2, -ch / 2);
          try {
            const rRes = await codeReader.decodeFromCanvas(rCanvas);
            if (rRes?.getText()) return rRes.getText();
          } catch (e) {}
        }
      } catch (err) {
        // ignore
      }

      return null;
    };

    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = async () => {
        try {
          const text = await decodeImage(img);
          if (text) { URL.revokeObjectURL(fileUrl); receiveTitipan(text); return; }

          const canvas = document.createElement('canvas');
          const scale = Math.min(2, Math.max(1, 1200 / Math.max(img.naturalWidth, img.naturalHeight)));
          canvas.width = Math.round(img.naturalWidth * scale);
          canvas.height = Math.round(img.naturalHeight * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          const enhancedImg = new Image();
          enhancedImg.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => { enhancedImg.onload = resolve; enhancedImg.onerror = reject; enhancedImg.src = canvas.toDataURL('image/png'); });
          const enhancedText = await decodeImage(enhancedImg);
          if (enhancedText) { URL.revokeObjectURL(fileUrl); receiveTitipan(enhancedText); return; }
          throw new Error('Tidak ada QR code');
        } catch (err) {
          setScannerError('Gagal memindai foto. Pastikan QR terlihat jelas.');
          Swal.fire({ icon: 'error', title: 'Gagal Memindai Foto', text: 'Pastikan QR terlihat jelas di foto.', confirmButtonText: 'Tutup' });
        }
      };
      img.onerror = () => {
        setScannerError('Gagal memuat foto.');
        Swal.fire({ icon: 'error', title: 'Gagal Memuat Foto', text: 'File gambar tidak dapat dimuat.', confirmButtonText: 'Tutup' });
      };
      img.src = fileUrl;
    } catch (err) {
      setScannerError('Gagal memproses foto.');
    }
  };

  // Pastikan kamera benar-benar mati saat komponen unmount dari manapun
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (scanMode !== 'camera') {
      stopCamera();
      return;
    }

    const container = readerContainerRef.current;
    if (!container) { setScannerError('Kontainer tidak tersedia.'); return; }

    let isMounted = true;
    let codeReader = null;

    const startScanner = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 300));
        const element = document.getElementById(readerContainerId);
        if (!element) { if (isMounted) setScannerError('Elemen scanner tidak ditemukan.'); return; }
        element.innerHTML = '';
        if (isMounted) setScannerError('');

        codeReader = new BrowserMultiFormatReader();
        codeReaderRef.current = codeReader;

        const onDecodeResult = (result) => {
          if (result?.getText && isMounted) {
            receiveTitipan(result.getText());
            try { codeReaderRef.current?.reset(); } catch (e) {}
          }
        };

        const captureStreamInfo = (videoEl) => {
          try {
            const stream = videoEl.srcObject;
            if (stream) {
              activeStreamRef.current = stream;
              const track = stream.getVideoTracks()[0];
              videoTrackRef.current = track;
              const caps = track.getCapabilities?.() || {};
              if (caps.torch) setFlashSupported(true);
            }
          } catch (e) {}
        };

        // Daftar constraints dari paling ketat → paling longgar
        const constraintList = [
          { audio: false, video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } } },
          { audio: false, video: { facingMode: 'environment' } },
          { audio: false, video: true },
        ];

        let started = false;
        for (const c of constraintList) {
          if (!isMounted) break;
          try {
            element.innerHTML = '';
            const vid = document.createElement('video');
            vid.setAttribute('autoplay', 'true');
            vid.setAttribute('muted', 'true');
            vid.setAttribute('playsinline', 'true');
            Object.assign(vid.style, { width: '100%', height: '100%', display: 'block', objectFit: 'cover', background: '#000' });
            element.appendChild(vid);
            activeVideoRef.current = vid;

            await codeReader.decodeFromConstraints(c, vid, onDecodeResult);
            captureStreamInfo(vid);
            started = true;
            break;
          } catch (err) {
            console.warn('Camera constraint failed, trying next:', c, err);
          }
        }

        if (!started && isMounted) {
          setScannerError('Tidak dapat mengakses kamera. Pastikan izin kamera diberikan.');
          Swal.fire({ icon: 'error', title: 'Kamera Tidak Tersedia', text: 'Pastikan izin kamera diberikan atau gunakan tombol galeri.', confirmButtonText: 'Tutup' });
        }
      } catch (err) {
        if (isMounted) setScannerError('Gagal menyiapkan scanner.');
      }
    };

    startScanner();

    return () => {
      isMounted = false;
      stopCamera();
    };
  }, [scanMode]);

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: '#0b1220',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Camera / Video area */}
      <div style={{ position: 'relative', flex: 1, backgroundColor: '#000', overflow: 'hidden' }}>
        {/* Video container */}
        <div
          ref={readerContainerRef}
          id={readerContainerId}
          style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
        />

        {/* Top bar overlay */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          padding: '14px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)',
          zIndex: 20,
        }}>
          {/* Back button */}
          <button
            onClick={handleCloseScan}
            style={{
              width: 42, height: 42, borderRadius: 14,
              border: 'none',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <ArrowLeft size={20} />
          </button>

          {/* Title */}
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 16, letterSpacing: 0.5 }}>
            Scan QR Handover
          </span>

          {/* Right buttons: Gallery + Flash */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => document.getElementById('lapor-scan-gallery-input').click()}
              style={{
                width: 42, height: 42, borderRadius: 14,
                border: 'none',
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
              title="Pilih dari Galeri"
            >
              <Image size={20} />
            </button>

            {flashSupported && (
              <button
                onClick={toggleFlash}
                style={{
                  width: 42, height: 42, borderRadius: 14,
                  border: 'none',
                  background: flashOn ? 'rgba(250,204,21,0.3)' : 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  color: flashOn ? '#facc15' : '#fff',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                title={flashOn ? 'Matikan Flash' : 'Nyalakan Flash'}
              >
                {flashOn ? <Zap size={20} fill="#facc15" /> : <ZapOff size={20} />}
              </button>
            )}
          </div>
        </div>

        {/* Scan frame / crosshair overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10, pointerEvents: 'none',
        }}>
          {/* Dark vignette with cutout */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            WebkitMaskImage: 'radial-gradient(ellipse 220px 220px at 50% 50%, transparent 0%, black 100%)',
            maskImage: 'radial-gradient(ellipse 220px 220px at 50% 50%, transparent 0%, black 100%)',
          }} />

          {/* Corner brackets */}
          {[
            { top: 0, left: 0, borderTop: '3px solid #3b82f6', borderLeft: '3px solid #3b82f6', borderRadius: '12px 0 0 0' },
            { top: 0, right: 0, borderTop: '3px solid #3b82f6', borderRight: '3px solid #3b82f6', borderRadius: '0 12px 0 0' },
            { bottom: 0, left: 0, borderBottom: '3px solid #3b82f6', borderLeft: '3px solid #3b82f6', borderRadius: '0 0 0 12px' },
            { bottom: 0, right: 0, borderBottom: '3px solid #3b82f6', borderRight: '3px solid #3b82f6', borderRadius: '0 0 12px 0' },
          ].map((style, i) => (
            <div key={i} style={{ position: 'absolute', width: 36, height: 36, ...style }} />
          ))}

          {/* Scan line animation */}
          <div style={{
            position: 'absolute',
            left: '50%', transform: 'translateX(-50%)',
            width: 200, height: 2,
            background: 'linear-gradient(to right, transparent, #3b82f6, transparent)',
            animation: 'scanline 2s ease-in-out infinite',
            borderRadius: 2,
          }} />
        </div>

        {/* Bottom gradient */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: 100,
          background: 'linear-gradient(to top, rgba(11,18,32,0.9) 0%, transparent 100%)',
          zIndex: 10, pointerEvents: 'none',
        }} />
      </div>

      {/* Bottom info bar */}
      <div style={{
        backgroundColor: '#0b1220',
        padding: '16px 20px 24px',
        textAlign: 'center',
        zIndex: 30,
      }}>
        {scannerError ? (
          <p style={{ color: '#facc15', fontSize: 13, margin: 0 }}>{scannerError}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#3b82f6' }}>
              <QrCode size={18} />
              <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>Arahkan ke QR Code</span>
            </div>
            <p style={{ color: '#64748b', fontSize: 12, margin: 0, lineHeight: 1.5 }}>
              Pastikan QR terlihat jelas dalam bingkai. Gunakan ikon galeri untuk memindai dari foto.
            </p>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        id="lapor-scan-gallery-input"
        type="file"
        accept="image/*"
        onChange={(e) => handlePhotoScan(e.target.files)}
        style={{ display: 'none' }}
      />

      {/* Scanline CSS animation */}
      <style>{`
        @keyframes scanline {
          0%   { top: calc(50% - 90px); opacity: 1; }
          50%  { top: calc(50% + 90px); opacity: 1; }
          100% { top: calc(50% - 90px); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default LaporScanPage;
